import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// "Acting as client" interceptor — when a recruiter selects a linked client
// from the recruiter dashboard, we stash that client_id in localStorage and
// auto-attach `X-Resmo-Acting-As: <client_id>` to every API call. The backend
// helper `resolve_acting_user` swaps the caller's identity for the client's
// where appropriate (analyze, strategy-brief, resume/base, etc.).
api.interceptors.request.use((config) => {
  try {
    const acting = window.localStorage.getItem("resmo:acting_as_client_id");
    if (acting) {
      config.headers = config.headers || {};
      config.headers["X-Resmo-Acting-As"] = acting;
    }
  } catch { /* localStorage may be blocked — ignore */ }
  return config;
});

// ── Sandbox cold-start handling ──────────────────────────────────────
// The preview/sandbox servers sleep when idle. The first request after a
// nap can fail with a network error or a 502/503/504 from the ingress
// while things warm up. Instead of leaking "Something went wrong", every
// error surface gets this friendly, non-scary message.
export const SANDBOX_WAKE_MSG =
  "Nothing's broken — this is a sandbox build and the servers sometimes need a minute to warm up. Give it ~30 seconds and try again.";

function isColdServerError(err) {
  const status = err?.response?.status;
  return !err?.response || status === 502 || status === 503 || status === 504;
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (isColdServerError(err) && !err?.response?.data?.detail) {
      err.response = err.response || { status: 0, headers: {}, data: {} };
      err.response.data = err.response.data || {};
      err.response.data.detail = SANDBOX_WAKE_MSG;
    }
    // Fire-and-forget error telemetry (5xx + network failures) so the admin
    // Beta Day Report can show which testers hit real errors. Never loops:
    // failures from the telemetry call itself are skipped and swallowed.
    try {
      const url = err?.config?.url || "";
      const status = err?.response?.status ?? 0;
      if (!url.includes("/telemetry/error") && (status === 0 || status >= 500)) {
        const detail = {
          path: window.location.pathname.slice(0, 200),
          url: url.slice(0, 250),
          status,
          message: String(err?.response?.data?.detail || err?.message || "unknown").slice(0, 400),
        };
        axios.post(`${API}/telemetry/error`, detail, { withCredentials: true }).catch(() => {});
        // Let the Resmo Helper know so it can proactively offer help.
        window.dispatchEvent(new CustomEvent("resmo:client-error", { detail }));
      }
    } catch { /* telemetry must never break the app */ }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Structured FastAPI/HTTPException dict — prefer human "message", fall
  // back to "error" code or stringified JSON so we never render
  // "[object Object]" to the user.
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
    try { return JSON.stringify(detail); } catch { return String(detail); }
  }
  return String(detail);
}

/**
 * Best-effort axios error → friendly string. Handles the common cluster
 * 502/504 timeout case where err.response is undefined and the bare axios
 * message ("Request failed with status code 502") leaks to the UI.
 */
export function friendlyAxiosError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail) return formatApiErrorDetail(detail);
  if (!err?.response || status === 502 || status === 503 || status === 504) {
    return SANDBOX_WAKE_MSG;
  }
  if (err?.code === "ECONNABORTED") {
    return SANDBOX_WAKE_MSG;
  }
  if (err?.message && err.message.startsWith("Request failed with status code")) {
    return "Something went wrong with that request. Please try again.";
  }
  return err?.message || "Something went wrong. Please try again.";
}
